import { AnswersByParticipant, OptionsApi } from '@core/swagger-client';
import { ClientType, QuestionType } from '@core/types';
import { Button, CompositePageHeader, ContainerNoTopSpace, ErrorMessagePage, Heading, LoaderPage, PageHeader, PageHeaderContent, Select } from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PollCard } from '../../molecules';

enum Lifecyle {
    Combined,
    Live,
    OnDemand
}

const emptyGuid = '00000000-0000-0000-0000-000000000000';

export const SurveyResults = () => {
    const { webinarId } = useParams({ strict: false });
    const [surveyAnswers, setSurveyAnswers] = useState<AnswersByParticipant[]>([]);
    const [lifecycle, setLifecycle] = useState<Lifecyle>(Lifecyle.Combined);
    const { t } = useTranslation();

    const { data: webinar, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!));
    const { data: liveSurveyAnswers, error: liveSurveyAnswersError } = useQuery(OptionsApi.insights.getLiveSurveyAnswers(webinarId!));
    const { data: onDemandSurveyAnswers, error: onDemandSurveyAnswersError } = useQuery(OptionsApi.insights.getOnDemandSurveyAnswers(webinarId!));
    const { data: survey, error: surveyError } = useQuery(OptionsApi.survey.get(webinarId!));

    useEffect(() => {
        if (!liveSurveyAnswers || !onDemandSurveyAnswers) {
            return;
        }

        let answers: AnswersByParticipant[] = [];
        if (lifecycle === Lifecyle.Live) {
            answers = liveSurveyAnswers;
        } else if (lifecycle === Lifecyle.OnDemand) {
            answers = onDemandSurveyAnswers;
        } else {
            answers = [...liveSurveyAnswers, ...onDemandSurveyAnswers];
        }
        setSurveyAnswers(answers);
    }, [liveSurveyAnswers, onDemandSurveyAnswers, lifecycle]);

    const error = webinarError ?? liveSurveyAnswersError ?? onDemandSurveyAnswersError ?? surveyError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (!webinar || !liveSurveyAnswers || !onDemandSurveyAnswers || !survey) {
        return <LoaderPage />;
    }

    const getAnsweredCount = (questionId: string) => {
        let count = 0;
        surveyAnswers.forEach((participant) => {
            const answer = participant.answers.find((a) => a.questionId === questionId);
            if (answer && (answer.selectedAnswers?.length || answer.openAnswer)) {
                count++;
            }
        });

        return count;
    };

    const getAnswerCount = (questionId: string, answerId?: string) => {
        let count = 0;
        surveyAnswers.forEach((participant) => {
            const answer = participant.answers.find((a) => a.questionId === questionId);
            if (!answer) {
                return;
            }

            if (answerId === undefined || answerId === emptyGuid) {
                if (answer.openAnswer) {
                    count++;
                }
            } else {
                if (answer.selectedAnswers && answer.selectedAnswers.includes(answerId)) {
                    count++;
                }
            }
        });

        return count;
    };

    // We cheat in this page by using Poll components to display survey results
    const getSurveyAnswers = (questionId: string) => {
        const question = survey.questions.find((q) => q.questionId === questionId);
        if (!question) {
            return {
                options: [],
                totalVotes: 0,
                totalWeightedVotes: 0
            };
        }

        const options = question.options.map((o, index) => {
            const answerCount = getAnswerCount(questionId, o.optionId);
            return {
                optionId: questionId + '_' + index,
                text: o.text,
                votes: answerCount,
                weightedVotes: answerCount,
                participants: []
            };
        });

        if (question.questionType === QuestionType.Open) {
            const answerCount = getAnswerCount(questionId);
            options.push({
                optionId: questionId + '_open',
                text: 'Open',
                votes: answerCount,
                weightedVotes: answerCount,
                participants: []
            });
        }

        if (question.hasOtherOption) {
            const answerCount = getAnswerCount(questionId);
            options.push({
                optionId: questionId + '_other',
                text: question.otherOptionText || '',
                votes: answerCount,
                weightedVotes: answerCount,
                participants: []
            });
        }

        return {
            options: options,
            totalVotes: getAnsweredCount(questionId),
            totalWeightedVotes: getAnsweredCount(questionId)
        };
    };

    const exportExcelAsync = async () => {
        const xlsx = await import('xlsx');
        const workbook = xlsx.utils.book_new();
        workbook.Props = {
            Application: 'OnlineWebinar.com',
            AppVersion: '1.0.0',
            Author: 'OnlineWebinar.com',
            CreatedDate: new Date(),
            Title: 'Survey results for ' + webinar.title
        };

        const summaryData: (string | number)[][] = [];
        survey.questions.forEach((question, questionIndex) => {
            summaryData.push([questionIndex + 1, question.questionText]);
            question.options.forEach((option) => {
                summaryData.push(['', option.text, getAnswerCount(question.questionId, option.optionId)]);
            });
            if (question.questionType === QuestionType.Open) {
                summaryData.push(['', 'Open', getAnswerCount(question.questionId)]);
            }
            if (question.hasOtherOption) {
                summaryData.push(['', question.otherOptionText || '', getAnswerCount(question.questionId)]);
            }
            summaryData.push([]);
        });

        // prettier-ignore
        const summary = xlsx.utils.aoa_to_sheet([
            ['Survey Results for ' + webinar.title],
            [],
            ['Exported on', , , , new Date()],
            [],
            ['Number of survey questions asked:', , , , survey.questions.length],
            [],
            ...summaryData
        ]);
        xlsx.utils.book_append_sheet(workbook, summary, 'Summary');

        survey.questions.forEach((question, questionIndex) => {
            // prettier-ignore
            const data: (number | string | undefined)[][] = [
                [`Details for ${questionIndex + 1}. ${question.questionText}`],
                [],
                ['First name', 'Last name', 'Nickname', 'Email address', 'Company', 'Country', 'Answer', 'Answer value', 'Client type', 'Time stamp'],
            ];

            surveyAnswers.forEach((surveyAnswer) => {
                const answer = surveyAnswer.answers.find((a) => a.questionId === question.questionId);
                if (!answer) {
                    return;
                }

                const participant: (number | string | undefined)[] = [
                    surveyAnswer.participant.firstName,
                    surveyAnswer.participant.lastName,
                    surveyAnswer.participant.nickname,
                    surveyAnswer.participant.emailAddress,
                    surveyAnswer.participant.companyName,
                    surveyAnswer.participant.countryCode
                ];
                answer.selectedAnswers?.forEach((optionId) => {
                    if (optionId === emptyGuid) return; // Skip emptyGuid
                    const option = question.options.find((o) => o.optionId === optionId);
                    data.push([...participant, option?.text, option?.value, ClientType[answer.clientType], answer.timestamp]);
                });
                if (answer.openAnswer) {
                    data.push([
                        ...participant,
                        question.hasOtherOption ? question.otherOptionText : 'Open',
                        answer.openAnswer,
                        ClientType[answer.clientType],
                        answer.timestamp
                    ]);
                }
            });

            const sheet = xlsx.utils.aoa_to_sheet(data);
            xlsx.utils.book_append_sheet(workbook, sheet, 'Question ' + (questionIndex + 1));
        });

        xlsx.writeFileXLSX(workbook, 'SurveyResults.xlsx');
    };

    const exportExcel = () => {
        exportExcelAsync().then(() => {
            // Done...
        });
    };

    return (
        <ContainerNoTopSpace>
            <div>
                <PageHeader>
                    <PageHeaderContent>
                        {webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}
                    </PageHeaderContent>
                </PageHeader>

                <div className="px:4 md:px-6 pt-4 md:pt-10 max-w-4xl">
                    <BackToOriginLink fallback="insights" />
                    <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                        {t('Insights.Survey.PageHeading')}
                    </Heading>

                    <Select
                        value={lifecycle}
                        onChange={(e) => setLifecycle(parseInt(e.target.value))}
                        label={t('Insights.Survey.Select.Label')}
                        classNames="max-w-sm mb-10"
                    >
                        <option value={Lifecyle.Combined}>{t('Insights.Survey.Select.Combined')}</option>
                        <option value={Lifecyle.Live}>{t('Insights.Survey.Select.Live')}</option>
                        <option value={Lifecyle.OnDemand}>{t('Insights.Survey.Select.OnDemand')}</option>
                    </Select>

                    <div className="flex justify-between flex-wrap gap-4 mb-8">
                        <div className="bg-white rounded-lg flex items-end py-2 px-4">
                            {t('Insights.Survey.AmountText')}
                            <span className="text-3xl font-medium pl-2">{survey.questions.length}</span>
                        </div>
                        <Button variant="primary" onClick={exportExcel}>
                            {t('Insights.Survey.Button.Export')}
                        </Button>
                    </div>
                    {survey.questions.map((question, index) => (
                        <PollCard
                            key={question.questionId}
                            poll={{
                                applyWeighting: false,
                                publicResults: false,
                                question: question.questionText,
                                pollId: question.questionId,
                                ...getSurveyAnswers(question.questionId)
                            }}
                            index={index}
                        ></PollCard>
                    ))}
                </div>
            </div>
        </ContainerNoTopSpace>
    );
};
